import { Injectable } from '@angular/core';

import {
  IPublicClientApplication,
  PublicClientApplication,
  AuthenticationResult,
  BrowserCacheLocation,
} from '@azure/msal-browser';
import { EMPTY, firstValueFrom, from, noop, Observable } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';

import { IMSOnlineEditingConfig } from '../types';
import { WkAppConfigService } from '../../services/wk-app-config.service';
import { WkAuthenticationService } from '../../services/wk-authentication.service';
import { MSONLINE_EDITING, MSONLINE_EDITING_ALLOWED } from '../constants';

@Injectable({
  providedIn: 'root',
})
export class MsalService {
  private static readonly DEFAULT_MSAL_CONFIG: IMSOnlineEditingConfig = {
    adapterUrl: '',
    msOnlineEditingAllowed: false,
    msalConfig: {
      clientId: '',
      authority: '',
      redirectUri: '',
    },
  };
  private static readonly SCOPES = [
    'user.read',
    'openid',
    'profile',
    'Files.ReadWrite.All',
  ];
  private pca: IPublicClientApplication;
  public adapterUrl = '';

  constructor(
    private readonly appConfigService: WkAppConfigService,
    private readonly authenticationService: WkAuthenticationService
  ) {
    this.initializeMsal();
    this.listenToLogout();
  }

  public fetchAccessToken(): Observable<string> {
    const currentAccount = this.pca?.getAllAccounts()?.[0];

    if (currentAccount) {
      return from(
        this.pca.acquireTokenSilent({
          account: currentAccount,
          scopes: MsalService.SCOPES,
        })
      ).pipe(
        map((response: AuthenticationResult) => response.accessToken),
        catchError(() =>
          this.pca
            .acquireTokenPopup({
              account: currentAccount,
              scopes: MsalService.SCOPES,
            })
            .then((response: AuthenticationResult) => response.accessToken)
            .catch((error) => {
              throw Error(error);
            })
        )
      );
    } else {
      return from(this.pca?.loginPopup({ scopes: MsalService.SCOPES })).pipe(
        map((response: AuthenticationResult) => response.accessToken),
        catchError((error) => {
          throw Error(error);
        })
      );
    }
  }

  private async initializeMsal(): Promise<void> {
    try {
      await firstValueFrom(this.appConfigService.onLoad.pipe(take(1)));
      const { adapterUrl, msOnlineEditingAllowed, msalConfig } =
        this.appConfigService.get<IMSOnlineEditingConfig>(
          MSONLINE_EDITING,
          MsalService.DEFAULT_MSAL_CONFIG
        );
      if (
        !this.isMsOnlineEditingAvailable({
          adapterUrl,
          msOnlineEditingAllowed,
          msalConfig,
        })
      ) {
        this.setMSALExtensionState();
        return;
      }
      this.adapterUrl = adapterUrl;
      if (
        !this.appConfigService.get<boolean>('legacyUrl', false) &&
        this.adapterUrl.startsWith('/')
      ) {
        this.adapterUrl = this.adapterUrl.slice(1);
      }
      this.pca = new PublicClientApplication({
        auth: msalConfig,
        cache: {
          cacheLocation: BrowserCacheLocation.LocalStorage,
          storeAuthStateInCookie: false,
        },
      });
      this.setMSALExtensionState(true);
    } catch {
      this.setMSALExtensionState();
    }
  }

  // Listens to logout events and clears MSAL browser storage
  private listenToLogout(): void {
    this.authenticationService.onLogout
      .pipe(switchMap(() => this.pca?.['browserStorage']?.clear() || EMPTY))
      .subscribe(noop);
  }

  private setMSALExtensionState(active = false): void {
    localStorage.setItem(MSONLINE_EDITING_ALLOWED, active ? 'true' : 'false');
  }

  // Checks if MS Online Editing is available based on the configuration
  private isMsOnlineEditingAvailable({
    adapterUrl,
    msOnlineEditingAllowed,
    msalConfig: { clientId, authority, redirectUri },
  }: IMSOnlineEditingConfig): boolean {
    return !!(
      msOnlineEditingAllowed &&
      adapterUrl &&
      clientId &&
      authority &&
      redirectUri
    );
  }
}
